import { useState, KeyboardEvent } from "react";
import { Flex, Image } from "@chakra-ui/react";
import { RectShape } from "react-placeholder/lib/placeholders";

/**
 * GEN-2407 R11 (4.3) — proporção real dos SVGs do switch (67×30). Declarada porque o `<Image>`
 * vinha só com `width`: até o SVG carregar a altura era 0, a linha "pulava", e o placeholder de
 * carregamento (que tinha altura fixa de 23px) deixava o controle com um tamanho diferente do
 * definitivo. Era isso que fazia o mesmo toggle parecer de tamanhos diferentes entre as telas.
 * Com `aspect-ratio` a altura é reservada antes do carregamento e acompanha qualquer `width`.
 */
export const SWITCH_ASPECT_RATIO = "67 / 30";

interface ISimpleSwitchProps {
  // Controlled value (if provided, o componente passa a ser controlado externamente)
  isActive?: boolean;
  // Valor inicial quando usado de forma não controlada
  defaultIsActive?: boolean;
  // Callback chamado sempre que o valor mudar
  onChange?: (value: boolean) => void;

  disabled?: boolean;
  isLoading?: boolean;

  // Estilo
  width?: string;

  // Acessibilidade
  id?: string;
  ariaLabel?: string;
}

export const Switch = ({
  isActive,
  defaultIsActive = false,
  onChange,
  disabled = false,
  isLoading = false,
  width = "50px",
  id,
  ariaLabel,
}: ISimpleSwitchProps) => {
  const [internalActive, setInternalActive] = useState<boolean>(defaultIsActive);

  const isControlled = typeof isActive === "boolean";
  const currentValue = isControlled ? (isActive as boolean) : internalActive;

  // R11 (4.3): caminho ABSOLUTO. Como `../icons/`, o ícone era resolvido a partir da URL da rota —
  // em rota de um nível funcionava, em rota mais profunda apontava para uma pasta que não existe e
  // o switch sumia.
  const image = currentValue ? "/icons/switch-blue.svg" : "/icons/switch-gray.svg";

  const handleToggle = () => {
    if (disabled || isLoading) return;

    const nextValue = !currentValue;

    if (!isControlled) {
      setInternalActive(nextValue);
    }

    onChange?.(nextValue);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleToggle();
    }
  };

  return (
    <Flex
      id={id}
      role="switch"
      aria-checked={currentValue}
      aria-disabled={disabled}
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : 0}
      display="inline-flex"
      alignItems="center"
      /**
       * GEN-2407 R15 (2) — SEGUNDA causa do mesmo sintoma que o R11 tratou acima: "o mesmo toggle
       * com tamanhos diferentes".
       *
       * O controle quase sempre vive num `Flex` ao lado de um RÓTULO ("Ativa" / "Inativa"), dentro
       * de uma célula de tabela com largura limitada. Sem `flex-shrink: 0` ele é um item flexível
       * como qualquer outro: quando o rótulo é mais LONGO, sobra menos espaço e o navegador encolhe
       * o switch. Resultado visível na tela de Consultas — a linha "Inativa" (7 caracteres) ficava
       * com o toggle menor que as linhas "Ativa" (5), na MESMA coluna. Não era o SVG nem o
       * `width`: era o controle cedendo espaço para o texto.
       *
       * `flexShrink={0}` fixa o tamanho pedido pelo `width` em qualquer largura de célula.
       */
      flexShrink={0}
      cursor={disabled || isLoading ? "not-allowed" : "pointer"}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
    >
      {isLoading ? (
        <RectShape
          color="#e0e0e0"
          style={{
            width,
            // Mesma proporção do switch definitivo: a altura fixa de 23px valia só para
            // `width="50px"` e trocava o tamanho do controle durante a escrita.
            aspectRatio: SWITCH_ASPECT_RATIO,
            borderRadius: "50px",
          }}
        />
      ) : (
        <Image
          src={image}
          width={width}
          sx={{ aspectRatio: SWITCH_ASPECT_RATIO }}
          opacity={disabled ? 0.3 : 1}
          draggable={false}
        />
      )}
    </Flex>
  );
};
